// Prices are in satang. This catalog does not grant storage or authorize a payment.
export const PRO_PLAN = Object.freeze({
  amount: 24_900,
  days: 31,
  label: 'BilaFlow Pro 31 วัน',
  baseStorageBytes: 5 * 1024 ** 3,
});

export const STORAGE_ADDON_OFFERS = Object.freeze([
  Object.freeze({ id: 'extra_5gb', extraBytes: 5 * 1024 ** 3, amount: 3_900 }),
  Object.freeze({ id: 'extra_20gb', extraBytes: 20 * 1024 ** 3, amount: 9_900 }),
  Object.freeze({ id: 'extra_50gb', extraBytes: 50 * 1024 ** 3, amount: 19_900 }),
] as const);

export type StorageAddonId = typeof STORAGE_ADDON_OFFERS[number]['id'];

export const formatBaht = (satang: number) => `฿${(satang / 100).toLocaleString('th-TH', { minimumFractionDigits: satang % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;

export interface StorageProrationQuote {
  offerId: StorageAddonId;
  amountToday: number;
  fullCycleAmount: number;
  combinedRenewalAmount: number;
  periodStart: number;
  periodEnd: number;
  calculatedAt: number;
  remainingSeconds: number;
}

export type StorageBillingPreview = { purchasesEnabled?: boolean } & ({
  available: true;
  renewalMode: 'automatic' | 'manual' | 'ending';
  validUntil: number;
  quotes: StorageProrationQuote[];
} | {
  available: false;
  reason: 'trial' | 'unverified_period' | 'inactive_pro' | 'payment_pending';
});

/** Unix seconds, supplied by the server. Round once to satang, without day rounding. */
export function prorateStorageAddon(offerId: unknown, periodStart: number, periodEnd: number, calculatedAt: number): StorageProrationQuote {
  const offer = STORAGE_ADDON_OFFERS.find((entry) => entry.id === offerId);
  if (!offer) throw new RangeError('invalid_storage_offer');
  if (![periodStart, periodEnd, calculatedAt].every((value) => Number.isSafeInteger(value) && value >= 0)
    || periodEnd - periodStart !== PRO_PLAN.days * 86400
    || calculatedAt < periodStart || calculatedAt >= periodEnd) throw new RangeError('invalid_billing_period');
  const duration = BigInt(periodEnd - periodStart);
  const numerator = BigInt(offer.amount) * BigInt(periodEnd - calculatedAt);
  return {
    offerId: offer.id,
    amountToday: Number((2n * numerator + duration) / (2n * duration)),
    fullCycleAmount: offer.amount,
    combinedRenewalAmount: PRO_PLAN.amount + offer.amount,
    periodStart,
    periodEnd,
    calculatedAt,
    remainingSeconds: periodEnd - calculatedAt,
  };
}

/** A comparison only: never persist this result as an account entitlement. */
export function previewStorageUpgrade(
  offerId: unknown,
  usage: { usedBytes: number; limitBytes: number | null },
) {
  const offer = STORAGE_ADDON_OFFERS.find((entry) => entry.id === offerId);
  if (!offer) throw new RangeError('invalid_storage_offer');
  if (!Number.isSafeInteger(usage.usedBytes) || usage.usedBytes < 0) throw new RangeError('invalid_storage_usage');
  // An override or a future entitlement must not silently become a standard Pro quote.
  if (usage.limitBytes !== PRO_PLAN.baseStorageBytes) return null;
  const totalBytes = PRO_PLAN.baseStorageBytes + offer.extraBytes;
  return {
    offerId: offer.id,
    extraBytes: offer.extraBytes,
    totalBytes,
    remainingBytes: Math.max(0, totalBytes - usage.usedBytes),
    overQuotaBytes: Math.max(0, usage.usedBytes - totalBytes),
    addonAmount: offer.amount,
    recurringAmount: PRO_PLAN.amount + offer.amount,
    periodDays: PRO_PLAN.days,
  };
}
